import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { driversService, jobsService } from '../../services';
import OptimizedRouteMap from './OptimizedRouteMap';
import './RouteOptimization.css';

const todayISO = () => new Date().toISOString().slice(0, 10);

// Pull the address+coords for whichever stop the job currently exposes.
// Backend marks Pickup vs Delivery on each formatted job.
function normalizeStop(job, idx) {
  const isPickup = job.stopType === 'Pickup';
  const block = isPickup ? job.pickup : job.delivery;
  return {
    key: `${job.id || job._id || idx}-${idx}`,
    sequence: idx + 1,
    jobCode: job.jobId || job.id || job._id,
    stopType: job.stopType || (isPickup ? 'Pickup' : 'Delivery'),
    name: block?.name || job.client?.name || 'Stop',
    address: block?.address || '',
    phone: block?.phone || '',
    coordinates: block?.coordinates || null,
    distance: job.distance, // km, hop from previous stop
    durationMinutes: job.durationMinutes,
    status: job.status,
  };
}

function formatDuration(totalMinutes) {
  if (!totalMinutes || totalMinutes < 1) return '—';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function RouteOptimization() {
  const { driverId: paramDriverId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(true);

  const [selectedDriverId, setSelectedDriverId] = useState(paramDriverId || '');
  const [date, setDate] = useState(searchParams.get('date') || todayISO());

  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load driver list once for the picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await driversService.getDrivers({ limit: 100 });
        if (cancelled) return;
        const list = data?.drivers || data?.users || data || [];
        setDrivers(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Load drivers error', err);
      } finally {
        if (!cancelled) setDriversLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadRoute = useCallback(async () => {
    if (!selectedDriverId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await jobsService.getDriverOptimizedRoute(selectedDriverId, { date });
      setRouteData(data);
    } catch (err) {
      console.error('Load optimized route error', err);
      setError(err.message || 'Failed to load route');
      setRouteData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedDriverId, date]);

  useEffect(() => {
    loadRoute();
  }, [loadRoute]);

  // Keep URL params in sync so the page is shareable.
  useEffect(() => {
    if (selectedDriverId && paramDriverId !== selectedDriverId) {
      navigate(`/route-optimization/${selectedDriverId}?date=${date}`, { replace: true });
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('date', date);
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverId, date]);

  const stops = useMemo(
    () => (routeData?.optimizedRoute || []).map(normalizeStop),
    [routeData],
  );

  const selectedDriver = useMemo(
    () => drivers.find((d) => (d._id || d.id) === selectedDriverId),
    [drivers, selectedDriverId],
  );

  const optimizedByGoogle = routeData?.optimizationMethod === 'google-directions';
  const fallbackUsed = routeData?.optimizationMethod === 'nearest-neighbor-haversine';

  return (
    <div className="mq-route-opt-page">
      <header className="mq-route-opt-header">
        <div>
          <h1>Route Optimization</h1>
          <p className="mq-route-opt-sub">
            Preview a driver's day in the most efficient driving order.
          </p>
        </div>

        <div className="mq-route-opt-controls">
          <label className="mq-route-opt-field">
            <span>Driver</span>
            <select
              value={selectedDriverId}
              onChange={(e) => setSelectedDriverId(e.target.value)}
              disabled={driversLoading}
            >
              <option value="">— Select a driver —</option>
              {drivers.map((d) => {
                const id = d._id || d.id;
                const name = `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.email;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </label>

          <label className="mq-route-opt-field">
            <span>Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>

          <button
            type="button"
            className="mq-route-opt-refresh"
            onClick={loadRoute}
            disabled={!selectedDriverId || loading}
          >
            {loading ? 'Optimizing…' : 'Re-optimize'}
          </button>
        </div>
      </header>

      {!selectedDriverId && (
        <div className="mq-route-opt-empty">
          Choose a driver to see their optimized stops.
        </div>
      )}

      {error && <div className="mq-route-opt-error">{error}</div>}

      {selectedDriverId && !loading && !error && stops.length === 0 && routeData && (
        <div className="mq-route-opt-empty">
          {routeData.message || 'No active stops for this driver on this date.'}
        </div>
      )}

      {stops.length > 0 && (
        <div className="mq-route-opt-summary">
          <div>
            <span className="mq-route-opt-summary-label">Stops</span>
            <span className="mq-route-opt-summary-value">{routeData.totalStops}</span>
          </div>
          <div>
            <span className="mq-route-opt-summary-label">Total distance</span>
            <span className="mq-route-opt-summary-value">
              {routeData.estimatedDistance ? `${routeData.estimatedDistance} km` : '—'}
            </span>
          </div>
          <div>
            <span className="mq-route-opt-summary-label">Total drive time</span>
            <span className="mq-route-opt-summary-value">
              {formatDuration(routeData.estimatedDuration)}
            </span>
          </div>
          <div className="mq-route-opt-method">
            {optimizedByGoogle && (
              <span className="mq-route-opt-badge mq-route-opt-badge-good">
                Google-optimized
              </span>
            )}
            {fallbackUsed && (
              <span className="mq-route-opt-badge mq-route-opt-badge-warn">
                Estimated (no traffic data)
              </span>
            )}
          </div>
        </div>
      )}

      <div className="mq-route-opt-body">
        <div className="mq-route-opt-map-col">
          <OptimizedRouteMap stops={stops} />
        </div>

        <div className="mq-route-opt-list-col">
          <h2>
            Stops{selectedDriver ? ` for ${selectedDriver.firstName || ''} ${selectedDriver.lastName || ''}`.trim() : ''}
          </h2>
          {stops.length === 0 ? (
            <div className="mq-route-opt-list-empty">No stops to show.</div>
          ) : (
            <ol className="mq-route-opt-stop-list">
              {stops.map((s) => (
                <li key={s.key} className="mq-route-opt-stop">
                  <div className="mq-route-opt-stop-num">{s.sequence}</div>
                  <div className="mq-route-opt-stop-body">
                    <div className="mq-route-opt-stop-head">
                      <span className={`mq-route-opt-tag mq-route-opt-tag-${(s.stopType || '').toLowerCase()}`}>
                        {s.stopType || 'Stop'}
                      </span>
                      <span className="mq-route-opt-stop-job">{s.jobCode}</span>
                    </div>
                    <div className="mq-route-opt-stop-name">{s.name}</div>
                    <div className="mq-route-opt-stop-address">{s.address}</div>
                    <div className="mq-route-opt-stop-meta">
                      {s.distance ? <span>{s.distance} km from previous</span> : null}
                      {s.distance && s.durationMinutes ? <span> · </span> : null}
                      {s.durationMinutes ? <span>{s.durationMinutes} min drive</span> : null}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
